import { ReactLenis, useLenis } from 'lenis/react'
import { motion } from 'motion/react'
import { startTransition, useEffect, useState } from 'react'
import {
	useFetcher,
	useLocation,
	useOutlet,
	useOutletContext,
	useRouteLoaderData,
} from 'react-router'
import { PageTransition } from '#app/components/marketing/page-transition.tsx'
import Splash from '#app/hoc/splash-screen.tsx'
import { type loader as rootLoader } from '#app/root.tsx'

function LenisController() {
	const lenis = useLenis()

	useEffect(() => {
		if (!lenis) return

		let isDialogOpen = false

		// Function to check dialog state and control scroll
		const checkDialog = () => {
			const iubendaIframe = document.getElementById('iubenda-iframe')
			const dialogIsOpen = iubendaIframe?.classList.contains(
				'iubenda-iframe-visible',
			)

			if (dialogIsOpen && !isDialogOpen) {
				// Dialog just opened
				console.log('Iubenda dialog opened - stopping Lenis')
				lenis.stop()

				// Find and configure dialog for scrolling
				setTimeout(() => {
					const purposesContent = document.querySelector('.purposes-content')
					const purposesContainer = document.getElementById(
						'purposes-content-container',
					)

					// Add data-lenis-prevent to prevent Lenis from capturing scroll events
					if (iubendaIframe) {
						iubendaIframe.setAttribute('data-lenis-prevent', '')
						console.log('Added data-lenis-prevent to iframe')
					}

					if (purposesContent) {
						const pc = purposesContent as HTMLElement
						pc.setAttribute('data-lenis-prevent', '')
						pc.style.overflowY = 'auto'
						pc.style.overflowX = 'hidden'
						pc.style.maxHeight = '100%'
					}

					if (purposesContainer) {
						const pct = purposesContainer as HTMLElement
						pct.setAttribute('data-lenis-prevent', '')
						pct.style.overflowY = 'auto'
						pct.style.overflowX = 'hidden'
						pct.style.maxHeight = '100%'
					}
				}, 100)

				isDialogOpen = true
			} else if (!dialogIsOpen && isDialogOpen) {
				// Dialog just closed
				console.log('Iubenda dialog closed - starting Lenis')
				lenis.start()
				document.documentElement.style.overflow = ''
				document.body.style.overflow = ''
				isDialogOpen = false
			}
		}

		// Poll every 200ms to check dialog state
		const pollInterval = setInterval(checkDialog, 200)

		// Also use MutationObserver for faster response
		const observer = new MutationObserver(() => {
			checkDialog()
		})

		// Start observing body for any changes
		observer.observe(document.body, {
			attributes: true,
			childList: true,
			subtree: true,
			attributeFilter: ['class', 'style'],
		})

		// Initial check
		checkDialog()

		return () => {
			clearInterval(pollInterval)
			observer.disconnect()
			// Restore scroll on cleanup
			lenis.start()
			document.documentElement.style.overflow = ''
			document.body.style.overflow = ''
		}
	}, [lenis])

	return null
}

const IS_PROD = process.env.NODE_ENV === 'production'

export default function MarketingLayout() {
	const route = useLocation()
	const context = useOutletContext()
	const outlet = useOutlet({
		context,
	})

	// Get splash state from root loader (server-side, no hydration delay!)
	const rootData = useRouteLoaderData<typeof rootLoader>('root')
	const splashSeen = rootData?.splashSeen ?? true // Default to true if no data

	// Track if splash animation completed this session
	const [showApp, setShowApp] = useState(!IS_PROD || splashSeen)

	// Force Lenis recalculation after route changes
	useEffect(() => {
		// Multiple resize attempts at different intervals
		// to catch the DOM at the right moment
		const timers = [
			setTimeout(() => window.dispatchEvent(new Event('resize')), 50),
			setTimeout(() => window.dispatchEvent(new Event('resize')), 150),
			setTimeout(() => window.dispatchEvent(new Event('resize')), 300),
		]

		return () => timers.forEach(clearTimeout)
	}, [route.pathname])

	const handleSplashComplete = () => {
		// Don't set showApp here - Framer Motion's onAnimationComplete will handle it
	}

	const fetcher = useFetcher()

	const handleAppAnimationComplete = () => {
		// Called when the app fade-in animation finishes
		startTransition(() => {
			setShowApp(true)
		})

		// Set cookie now that both animations are complete
		void fetcher.submit(null, {
			method: 'POST',
			action: '/resources/splash-seen',
		})
	}

	if (route.pathname.includes('search'))
		return (
			<>
				{IS_PROD && !showApp && (
					<div className="fixed inset-0 z-[100]">
						<Splash onComplete={handleSplashComplete} />
					</div>
				)}
				<motion.div
					initial={IS_PROD && !splashSeen ? { opacity: 0 } : undefined}
					animate={
						IS_PROD && !splashSeen
							? {
									opacity: 1,
									transition: {
										// Start fading in AFTER splash finishes (2100ms)
										delay: 2.1,
										duration: 0.4,
									},
								}
							: undefined
					}
					onAnimationComplete={
						IS_PROD && !splashSeen ? handleAppAnimationComplete : undefined
					}
					className={
						IS_PROD && !splashSeen && !showApp ? 'invisible' : undefined
					}
				>
					{outlet}
				</motion.div>
			</>
		)

	return (
		<>
			{IS_PROD && !showApp && (
				<div className="fixed inset-0 z-[100]">
					<Splash onComplete={handleSplashComplete} />
				</div>
			)}
			<motion.div
				initial={IS_PROD && !splashSeen ? { opacity: 0 } : undefined}
				animate={
					IS_PROD && !splashSeen
						? {
								opacity: 1,
								transition: {
									// Start fading in AFTER splash finishes (2100ms)
									delay: 2.1,
									duration: 0.4,
								},
							}
						: undefined
				}
				onAnimationComplete={
					IS_PROD && !splashSeen ? handleAppAnimationComplete : undefined
				}
				className={IS_PROD && !splashSeen && !showApp ? 'invisible' : undefined}
			>
				<ReactLenis
					root
					options={{
						lerp: 0.07,
					}}
				>
					<LenisController />
					<PageTransition>{outlet}</PageTransition>
				</ReactLenis>
			</motion.div>
		</>
	)
}
